import { issueStatusLabel } from "@/components/issues/issue-status-pill/issue-status-pill";
import { personDisplayName } from "@/lib/people";
import type { IssueStatus, ProjectUser } from "@/types/api";

/** The Issues page's view, as the name prefill reads it. */
export interface ReportNameFilters {
  /** The status tab; null is All. */
  status: IssueStatus | null;
  overdue: boolean;
  /** User ids, or null for anyone. */
  assignedTo: string | null;
  createdBy: string | null;
  search: string;
}

/**
 * A report name built from the filters on screen, so nobody types one on a
 * jobsite that the filter already says: "Overdue · Jason Lim", "In progress ·
 * Raised by Priya Nair". Empty when nothing is filtered — the dialog then asks
 * for a name, as it always has.
 *
 * No date: a report is re-exported for weeks and the Reports list already shows
 * each export's date. A person no longer on the project contributes nothing
 * rather than an id. See [[issues-report-dialog.spec]] US-002.
 */
export function reportNameFromFilters(
  filters: ReportNameFilters,
  people: readonly ProjectUser[],
): string {
  const nameOf = (id: string | null): string | null => {
    if (!id) return null;
    const person = people.find((candidate) => candidate.id === id);
    return person ? personDisplayName(person) : null;
  };

  const parts: string[] = [];
  if (filters.status) parts.push(issueStatusLabel(filters.status));
  if (filters.overdue) parts.push("Overdue");
  const assignee = nameOf(filters.assignedTo);
  if (assignee) parts.push(assignee);
  const raiser = nameOf(filters.createdBy);
  if (raiser) parts.push(`Raised by ${raiser}`);
  const search = filters.search.trim();
  if (search) parts.push(`“${search}”`);
  return parts.join(" · ");
}
